import { trigger, state, style, transition, animate, group, query, animateChild } from '@angular/animations';
import * as i0 from '@angular/core';
import { Directive, EventEmitter, Component, Input, Output, HostBinding, HostListener, ElementRef, ViewChildren, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

const TIMING = '500ms cubic-bezier(0.35, 0, 0.25, 1)';
const dialogAnimations = [
    trigger('stagePosition', [
        // This commented out code is the original animation for this app
        // which used a slide across style animation for dialogs.
        // They are being preserved (as a comment), should a future app
        // lend itself to these animations instead of the current
        /*state('center', style({transform: 'none'})),
        state('before', style({transform: 'translate3d(-100%, 0, 0)'})),
        state('after', style({transform: 'translate3d(100%, 0, 0)'})),*/
        // These state definitions are the current fade between style
        // which is more visually appearling
        state('center', style({ opacity: 1 })),
        state('before', style({ opacity: 0 })),
        state('after', style({ opacity: 0 })),
        transition('* => *', [animate(TIMING)]),
    ]),
    trigger('stageWrapper', [
        state('*', style({ height: '*', width: '*' })),
        transition(':enter, :leave', []),
        transition('* => *', [
            style({ height: '{{startHeight}}px', width: '{{startWidth}}px' }),
            group([
                animate(TIMING),
                query('@stagePosition', [animateChild({ delay: '0' })]),
            ]),
        ]),
    ]),
    trigger('animationSink', [transition('* => *', [])]),
];

class StageBaseDirective {
    constructor(template) {
        this.template = template;
    }
}
StageBaseDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: StageBaseDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
StageBaseDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.0.0", type: StageBaseDirective, selector: "[dmcStageDirectiveBase]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: StageBaseDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[dmcStageDirectiveBase]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class StageBaseComponent {
    constructor() {
        this.currentIndex = -2;
        this.index = -1;
        this.afterStageEnter = new EventEmitter();
    }
    get stagePosition() {
        if (this.currentIndex === this.index) {
            return 'center';
        }
        else if (this.currentIndex > this.index) {
            return 'before';
        }
        else {
            return 'after';
        }
    }
    stagePositionDone() {
        if (this.currentIndex === this.index) {
            this.afterStageEnter.emit();
        }
    }
    get activeClass() {
        return this.currentIndex === this.index;
    }
    ngAfterViewInit() {
        if (this.activeClass) {
            setTimeout(() => this.afterStageEnter.emit());
        }
    }
}
StageBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: StageBaseComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
StageBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.0.0", type: StageBaseComponent, selector: "dmc-stage-base", inputs: { currentIndex: "currentIndex", index: "index", template: "template" }, outputs: { afterStageEnter: "afterStageEnter" }, host: { listeners: { "@stagePosition.done": "stagePositionDone()" }, properties: { "@stagePosition": "this.stagePosition", "class.active": "this.activeClass" } }, ngImport: i0, template: "" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: StageBaseComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dmc-stage-base',
                    templateUrl: './stage-base.component.html',
                    styles: [],
                }]
        }], propDecorators: { currentIndex: [{
                type: Input
            }], index: [{
                type: Input
            }], template: [{
                type: Input
            }], afterStageEnter: [{
                type: Output
            }], stagePosition: [{
                type: HostBinding,
                args: ['@stagePosition']
            }], stagePositionDone: [{
                type: HostListener,
                args: ['@stagePosition.done']
            }], activeClass: [{
                type: HostBinding,
                args: ['class.active']
            }] } });

class StageGroupBaseComponent {
    constructor() {
        this.stage = null;
        this.afterStageChange = new EventEmitter();
    }
    get _selectedIndex() {
        if (!this.stages) {
            return 0;
        }
        else {
            const directive = this.stages.find((s) => s.name === this.stage);
            return directive ? this.stages.toArray().indexOf(directive) : -1;
        }
    }
    ngOnChanges(changes) {
        if ('stage' in changes) {
            this.stageWrapper = {
                value: changes.stage.currentValue,
                params: this.stageWrappers
                    ? this.computeCurrentSize()
                    : {
                        startHeight: 0,
                        startWidth: 0,
                    },
            };
            // do this after setting the tabWrapper, so that computeCurrentSize
            // uses the previous value to compute the index (this gets the size
            // of the current item).
            this.stage = changes.stage.currentValue;
        }
    }
    computeCurrentSize() {
        const el = this.stageWrappers.toArray()[this._selectedIndex]
            .nativeElement;
        return {
            startHeight: el.scrollHeight,
            startWidth: el.scrollWidth,
        };
    }
}
StageGroupBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: StageGroupBaseComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
StageGroupBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.0.0", type: StageGroupBaseComponent, selector: "dmc-stage-group-base", inputs: { stage: "stage" }, outputs: { afterStageChange: "afterStageChange" }, host: { properties: { "@stageWrapper": "this.stageWrapper" } }, viewQueries: [{ propertyName: "stageWrappers", predicate: ["stageWrapper"], descendants: true, read: ElementRef }], usesOnChanges: true, ngImport: i0, template: "" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: StageGroupBaseComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dmc-stage-group-base',
                    templateUrl: './stage-group-base.component.html',
                    styles: [],
                }]
        }], propDecorators: { stage: [{
                type: Input
            }], stageWrappers: [{
                type: ViewChildren,
                args: ['stageWrapper', { read: ElementRef }]
            }], stageWrapper: [{
                type: HostBinding,
                args: ['@stageWrapper']
            }], afterStageChange: [{
                type: Output
            }] } });

// "public" modules
// these are only for use within this library
const publicComponents = [
    StageBaseComponent,
    StageGroupBaseComponent,
    StageBaseDirective,
];
class DmcCoreModule {
}
DmcCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: DmcCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DmcCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: DmcCoreModule, declarations: [StageBaseComponent,
        StageGroupBaseComponent,
        StageBaseDirective], imports: [CommonModule], exports: [StageBaseComponent,
        StageGroupBaseComponent,
        StageBaseDirective] });
DmcCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: DmcCoreModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.0", ngImport: i0, type: DmcCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...publicComponents],
                    imports: [CommonModule],
                    exports: [...publicComponents],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DmcCoreModule, StageBaseComponent, StageBaseDirective, StageGroupBaseComponent, dialogAnimations };
